import { ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { motion } from "framer-motion";

export default function Hero({ site }) {
  const h = site?.hero || {};
  const stats = site?.stats || [];

  const go = (href) => {
    if (href?.startsWith("#")) document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative pt-[120px] pb-16 lg:pt-[150px] lg:pb-24 overflow-hidden paper-texture">
      <div className="absolute inset-0 bg-noise pointer-events-none" />
      <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[var(--c-secondary)]/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center relative">
        <div>
          <Reveal><SectionLabel>{h.label || "কণ্ঠে কবিতা, মনে সংস্কৃতি"}</SectionLabel></Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-heading font-extrabold text-[var(--c-primary)] mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.15] tracking-tight">
              {h.heading}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-body text-base sm:text-lg text-[var(--c-muted)] mt-6 max-w-xl leading-relaxed">
              {h.subheading}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="flex flex-wrap gap-4 mt-9">
              {h.primaryBtn?.label && (
                <button
                  data-testid="hero-primary-btn"
                  onClick={() => go(h.primaryBtn.href)}
                  className="group inline-flex items-center gap-2 rounded-full px-8 py-4 font-body font-medium text-white bg-[var(--c-primary)] hover:bg-[var(--c-secondary)] transition-colors hover:-translate-y-0.5 duration-300"
                >
                  {h.primaryBtn.label}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
              {h.secondaryBtn?.label && (
                <button
                  data-testid="hero-secondary-btn"
                  onClick={() => go(h.secondaryBtn.href)}
                  className="inline-flex items-center rounded-full px-8 py-4 font-body font-medium text-[var(--c-primary)] border border-[var(--c-accent)] hover:bg-[var(--c-bg-alt)] transition-colors"
                >
                  {h.secondaryBtn.label}
                </button>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="flex gap-10 mt-14">
              {stats.map((s, i) => (
                <div key={i}>
                  <div className="font-heading text-3xl sm:text-4xl font-bold text-[var(--c-secondary)]">{s.value}</div>
                  <div className="font-body text-sm text-[var(--c-muted)] mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.25} y={40}>
          <div className="relative">
            <div className="absolute inset-0 -m-3 rounded-[28px] border border-[var(--c-accent)]/40" />
            <div className="relative rounded-[24px] overflow-hidden shadow-[0_30px_80px_-20px_rgba(106,27,26,0.35)]">
              <img
                src={h.image}
                alt={h.heading}
                loading="lazy"
                className="w-full h-[420px] sm:h-[540px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--c-primary)]/70 via-transparent to-transparent" />
              {h.captionText && (
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="font-body text-[11px] tracking-[0.3em] opacity-80">{h.captionLabel}</div>
                  <div className="font-heading text-xl sm:text-2xl font-semibold mt-1">{h.captionText}</div>
                </div>
              )}
            </div>
            {h.badgeTitle && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="absolute -left-4 sm:-left-10 top-24 bg-[var(--c-card)] rounded-2xl border border-[var(--c-border)] shadow-xl px-5 py-3 flex items-center gap-3"
              >
                <span className="grid place-items-center h-10 w-10 rounded-full bg-[var(--c-primary)] text-white font-heading">শ্রু</span>
                <div>
                  <div className="font-body text-[11px] text-[var(--c-muted)]">{h.badgeTitle}</div>
                  <div className="font-heading text-sm font-semibold text-[var(--c-primary)]">{h.badgeText}</div>
                </div>
              </motion.div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
